import { useState } from "react";
import { StarRating } from "./StarRating";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Check, ExternalLink, Loader2 } from "lucide-react";

interface PublicReviewPageProps {
  businessName: string;
  logoUrl?: string;
  staffName?: string;
  jobDescription?: string;
  jobPhotoUrl?: string;
  googleReviewUrl: string;
  brandColor?: string;
  onSubmitFeedback: (rating: number, feedback: string) => void;
}

export function PublicReviewPage({
  businessName,
  logoUrl,
  staffName,
  jobDescription,
  jobPhotoUrl,
  googleReviewUrl,
  brandColor = "#3B82F6",
  onSubmitFeedback,
}: PublicReviewPageProps) {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [step, setStep] = useState<"rating" | "feedback" | "thanks">("rating");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingSelect = (selectedRating: number) => {
    setRating(selectedRating);
    // All users go to feedback step regardless of rating (Google-compliant)
    setStep("feedback");
  };

  const handleSubmitFeedback = async () => {
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    onSubmitFeedback(rating, feedback);
    setIsSubmitting(false);
    setStep("thanks");
  };

  const staffInitials = staffName
    ? staffName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="public-review-page">
        <div
          className="h-2 rounded-t-md"
          style={{ backgroundColor: brandColor }}
        />
        <CardContent className="pt-8 pb-8 px-6">
          <div className="flex flex-col items-center text-center">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={businessName}
                className="h-16 w-auto mb-4"
              />
            ) : (
              <h1 className="text-2xl font-semibold mb-4">{businessName}</h1>
            )}

            {step === "rating" && (
              <>
                <p className="text-lg mb-6">How was your experience?</p>
                
                {staffName && (
                  <div className="flex items-center gap-3 mb-6 p-3 bg-muted/50 rounded-md">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {staffInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <p className="font-medium">{staffName}</p>
                      {jobDescription && (
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {jobDescription}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {jobPhotoUrl && (
                  <img
                    src={jobPhotoUrl}
                    alt="Job completed"
                    className="w-full max-w-xs rounded-md mb-6"
                  />
                )}

                <StarRating value={rating} onChange={handleRatingSelect} size="lg" />
                <p className="text-sm text-muted-foreground mt-4">
                  Tap a star to rate your experience
                </p>
              </>
            )}

            {step === "feedback" && (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <StarRating value={rating} onChange={() => {}} size="sm" readonly />
                </div>
                <p className="text-lg mb-4">
                  Would you like to share any additional feedback?
                </p>
                <Textarea
                  placeholder="Tell us about your experience... (optional)"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="mb-4"
                  rows={4}
                  data-testid="textarea-feedback"
                />
                <Button
                  onClick={handleSubmitFeedback}
                  className="w-full"
                  disabled={isSubmitting}
                  data-testid="button-submit-feedback"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    feedback.trim() ? "Submit & Continue" : "Continue"
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setStep("rating");
                    setRating(0);
                  }}
                  className="mt-2"
                  data-testid="button-back"
                >
                  Change Rating
                </Button>
              </>
            )}

            {step === "thanks" && (
              <>
                <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-semibold mb-2">Thank you!</h2>
                <p className="text-muted-foreground mb-6">
                  We appreciate your feedback and will use it to improve our service.
                </p>
                <Button
                  onClick={() => window.open(googleReviewUrl, "_blank")}
                  className="w-full"
                  data-testid="button-google-review"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Leave a Google Review
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  Your honest review helps other customers find great local businesses
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
